import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/hooks/use-toast";
import { supplierPaymentService } from "~/services/purchasing";

import type { PaySupplierInvoicesInput } from "~/types/purchasing";

export const SUPPLIER_PAYABLE_DOCS_KEY = "supplier-payable-documents";

/** Journal code used for every supplier-payment draft asiento created by `pay_supplier_invoices`.
 * A borrador in this journal is a "pago nómina proveedores" that can be returned to Pagos a
 * Proveedor (reversing the payment). */
export const SUPPLIER_PAYMENTS_JOURNAL_CODE = "PAGO_PROVEEDORES NACIONALES";

/** All open payable documents across suppliers (facturas + saldo inicial), optionally per empresa. */
export function useSupplierPayableDocuments(companyId?: string) {
  return useQuery({
    queryKey: [SUPPLIER_PAYABLE_DOCS_KEY, companyId ?? "all"],
    queryFn: () => supplierPaymentService.getPayableDocuments(companyId),
  });
}

/** Pagar factura(s): create the draft asiento (PAGO_PROVEEDORES journal) + rebajar el saldo, then
 * refresh invoices, balances and accounting. Publish the draft from Movimientos. */
export function usePaySupplierInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PaySupplierInvoicesInput) =>
      supplierPaymentService.pay(input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["supplier-balances"] });
      queryClient.invalidateQueries({ queryKey: [SUPPLIER_PAYABLE_DOCS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["movements"] });
      const n = result.total;
      toast({
        title: "Pago registrado",
        description: `Se creó el asiento en borrador por $${n.toLocaleString(
          "es-CL"
        )}. Revísalo y publícalo en Movimientos.`,
      });
    },
    onError: (e: Error) =>
      toast({
        title: "No se pudo registrar el pago",
        description: e.message,
        variant: "destructive",
      }),
  });
}

/** Devolver un borrador de pago (asiento en borrador del diario PAGO_PROVEEDORES NACIONALES) a
 * "Pagos a Proveedor": revierte el pago y los documentos vuelven a quedar pendientes de pago.
 * Reutiliza el soft-delete del asiento, que dispara la reversión del saldo en la base de datos. */
export function useReturnSupplierPaymentDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (movementId: string) =>
      supplierPaymentService.returnDraft(movementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["supplier-balances"] });
      queryClient.invalidateQueries({ queryKey: [SUPPLIER_PAYABLE_DOCS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["movements"] });
      toast({
        title: "Borrador devuelto a Pagos a Proveedor",
        description:
          "Se revirtió el pago y los documentos volvieron a quedar pendientes de pago.",
      });
    },
    onError: (e: Error) =>
      toast({
        title: "No se pudo devolver el borrador",
        description: e.message,
        variant: "destructive",
      }),
  });
}
